import React, { useState } from 'react';
import { Box, Container, Typography } from '@mui/material';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import ComplianceDetailsDrawer from '../components/ComplianceDetailsDrawer';
import ComplianceTable, { ComplianceTransaction } from '../components/ComplianceTable';
import { useToast } from '../components/ui/ToastProvider';
// Mock data
const MOCK_COMPLIANCE_TRANSACTIONS: ComplianceTransaction[] = Array(25).fill(null).map((_, index) => {
  const payoutTypes: ('FT' | 'IBFT' | 'OTC')[] = ['FT', 'IBFT', 'OTC'];
  const payoutType = payoutTypes[index % 3];
  const baseTime = Date.now() - index * 3600000;
  const agentCodes = ['Ecobank Ghana', 'Ecobank Nigeria', 'Ecobank Senegal', 'Ecobank Uganda', 'Ecobank Kenya', "Ecobank Côte d'Ivoire", 'Ecobank Togo'];
  const branchCodes = ['GH-ACC-001', 'GH-KUM-001', 'NG-LAG-001', 'NG-ABJ-001', 'SN-DKR-001', 'UG-KLA-001', 'KE-NBI-001', 'CI-ABJ-001'];
  return {
    txnId: `TXN-${15000 + index}`,
    mtoName: ['GTBank Ghana', 'Flutterwave Nigeria', 'M-Pesa Kenya', 'Orange Money Senegal'][index % 4],
    payoutType,
    beneficiaryName: ['Samuel Mensah', 'David Johnson', 'Marie Dupont', 'Klaus Schmidt', 'Michael Brown'][index % 5],
    beneficiaryCnic: `P${String(1000000 + index).padStart(7, '0')}`,
    beneficiaryTitle: `${String(index).padStart(16, '0')}`,
    amountFcy: `${(500 + index * 25).toFixed(2)}`,
    currencyFcy: 'USD',
    amountPkr: `${((500 + index * 25) * 1500).toLocaleString()}`,
    createdAt: new Date(baseTime - 5000).toISOString(),
    lastUpdateAt: new Date(baseTime).toISOString(),
    lifecycle: 'IN_REVIEW',
    currentState: 'SCREENING',
    mtoRef: `MTO-${25000 + index}`,
    bankRef: `BNK-${35000 + index}`,
    branchCode: payoutType === 'OTC' ? branchCodes[index % branchCodes.length] : '',
    agentCode: payoutType === 'OTC' ? agentCodes[index % agentCodes.length] : '',
    railRefs: {},
    screening: {
      engine: 'WorldCheck',
      score: 75 + index % 20,
      hits: ['Potential PEP match', 'Name similarity with sanctioned entity', 'High-risk jurisdiction'].slice(0, index % 3 + 1),
      failReasonCode: 'MANUAL_REVIEW_REQUIRED',
      failReasonText: 'Transaction flagged for manual review due to screening alerts',
      screenedAt: new Date(baseTime - 2000).toISOString()
    },
    remitterName: ['Kwame Asante', 'Adekunle Okoro', 'Fatoumata Ndiaye', 'Grace Kipchoge', 'Amara Jalloh'][index % 5],
    remitterIdType: 'Passport',
    remitterIdNumber: `P${String(2000000 + index).padStart(7, '0')}`,
    remitterCountry: ['Ghana', 'Nigeria', 'Senegal', 'Kenya'][index % 4],
    beneficiaryIban: payoutType === 'IBFT' ? `GB29NWBK601613319268${String(index).padStart(2, '0')}` : undefined,
    beneficiaryBank: payoutType === 'IBFT' ? ['Ecobank Ghana', 'Ecobank Nigeria', 'Ecobank Senegal', 'Ecobank Kenya'][index % 4] : undefined,
    purposeCode: ['FAM', 'SAL', 'EDU', 'MED'][index % 4],
    purposeText: ['Personal Remittance', 'Business Payment', 'Education', 'Medical'][index % 4],
    fxRate: 1500 + index % 5 * 5,
    amendAllowed: true,
    cancelAllowed: true,
    amendStatus: 'NONE',
    cancelStatus: 'NONE'
  } as any;
});
interface ComplianceReviewProps {
  onLogout: () => void;
}
const ComplianceReview: React.FC<ComplianceReviewProps> = ({
  onLogout
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [transactions, setTransactions] = useState<ComplianceTransaction[]>(MOCK_COMPLIANCE_TRANSACTIONS);
  const [selectedTransaction, setSelectedTransaction] = useState<ComplianceTransaction | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const {
    showToast
  } = useToast();
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleRowClick = (transaction: ComplianceTransaction) => {
    setSelectedTransaction(transaction);
    setDrawerOpen(true);
  };
  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedTransaction(null);
  };
  const handleTransactionRemoval = (txnId: string) => {
    setTransactions(prev => prev.filter(txn => txn.txnId !== txnId));
    handleCloseDrawer();
  };
  const handleExport = () => {
    showToast('CSV export initiated', 'success');
  };
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#F8FAFC'
  }} data-id="element-2962">
      <TopBar onMenuClick={handleDrawerToggle} onLogout={onLogout} data-id="element-2963" />
      <Sidebar open={mobileOpen} onClose={handleDrawerToggle} activePage="compliance" data-id="element-2964" />
      <Box component="main" sx={{
      flexGrow: 1,
      py: 2,
      px: 1,
      width: {
        sm: `calc(100% - 240px)`
      },
      ml: 0,
      mt: 8
    }} data-id="element-2965">
        <Container maxWidth="xl" data-id="element-2966">
          <Box sx={{
          mb: 3
        }} data-id="element-2967">
            <Typography variant="h4" fontWeight={600} gutterBottom data-id="element-2968">
              Compliance Review / Screening
            </Typography>
            <Typography variant="body1" color="text.secondary" data-id="element-2969">
              Review and approve transactions flagged for manual screening
            </Typography>
          </Box>
          <ComplianceTable transactions={transactions} onRowClick={handleRowClick} onExport={handleExport} data-id="element-2970" />
        </Container>
      </Box>
      {selectedTransaction && <ComplianceDetailsDrawer open={drawerOpen} onClose={handleCloseDrawer} transaction={selectedTransaction} onTransactionRemoval={handleTransactionRemoval} data-id="element-2971" />}
    </Box>;
};
export default ComplianceReview;